import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { CAL } from '@/constants/testIds';
import { AlertTriangle, Loader2, UserPlus, Search, X } from 'lucide-react';

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


/**
 * PatientTypeahead — debounced server-side search over 47k+ patients.
 * Renders MAX 20 results at a time (prevents the "site fully hangs when
 * opening New Appointment" bug caused by a native <Select> that tried to
 * mount 47,000 <SelectItem> nodes at once).
 */
function PatientTypeahead({ value, seedPatients, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  // Resolve the selected patient's display label — try seed cache first,
  // fall back to a single-shot API GET so patient names never render blank.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    const hit = (seedPatients || []).find(p => p.id === value);
    if (hit) { setSelected(hit); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/patients/${value}`);
        if (!cancelled) setSelected(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [value, seedPatients]);

  // Debounced server search — trims payload, caps at 20 rows.
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}&limit=20`);
        setResults(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // Click-outside to close
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={rootRef} className="relative flex-1" data-testid="create-apt-patient-typeahead">
      {selected && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full h-9 px-2.5 border border-stone-300 rounded-md text-sm text-left bg-white hover:bg-stone-50 flex items-center gap-2"
          data-testid="create-apt-patient-selected"
        >
          <Search className="w-3.5 h-3.5 text-stone-400 shrink-0" />
          <span className="flex-1 truncate">{selected.full_name} · <span className="text-stone-500 font-mono">{selected.mobile}</span></span>
          <X className="w-3.5 h-3.5 text-stone-400 hover:text-stone-700"
             onClick={(e) => { e.stopPropagation(); onChange(''); setSelected(null); setQ(''); setOpen(true); }} />
        </button>
      ) : (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400 pointer-events-none" />
          <Input
            autoFocus={open}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search patient by name or mobile…"
            className="h-9 pl-8 text-sm"
            data-testid="create-apt-patient-search"
          />
        </div>
      )}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 border border-stone-200 rounded-md bg-white shadow-lg z-50 max-h-64 overflow-y-auto"
             data-testid="create-apt-patient-results">
          {loading && (
            <div className="px-3 py-2 text-xs text-stone-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-stone-400">
              {q ? 'No matches — try a different query, or use ➕ to add a new patient' : 'Start typing name or mobile…'}
            </div>
          )}
          {!loading && results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onChange(p.id); setSelected(p); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-1.5 hover:bg-stone-50 text-sm border-b last:border-b-0 border-stone-100"
              data-testid={`create-apt-patient-opt-${p.id}`}
            >
              <div className="truncate">{p.full_name}</div>
              <div className="text-[11px] text-stone-500 font-mono">{p.mobile || '—'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CreateAppointmentDialog({
  open, onOpenChange, clinics, employees, patients, rooms, aptTypes,
  defaultDate, onCreated, onPatientCreated,
}) {
  const [form, setForm] = useState({
    patient_id: '',
    appointment_type_id: '',
    clinic_id: '',
    audiologist_id: '',
    room_id: '',
    channel: 'in_clinic',
    start_ts: '',
    duration_minutes: 30,
    priority: 'normal',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);

  // New patient sub-form
  const [showNewPatient, setShowNewPatient] = useState(false);
  const [newPatient, setNewPatient] = useState({
    full_name: '', mobile: '', age: '', gender: 'male', lead_source: 'Walk-in',
  });

  useEffect(() => {
    if (open) {
      const start = new Date(defaultDate || new Date());
      start.setMinutes(0, 0, 0);
      if (start.getHours() < 9) start.setHours(9);
      setForm({
        patient_id: '', appointment_type_id: '', clinic_id: clinics[0]?.id || '',
        audiologist_id: '', room_id: '', channel: 'in_clinic',
        start_ts: toLocalInput(start), duration_minutes: 30,
        priority: 'normal', notes: '',
      });
      setConflicts([]);
      setOverride(false);
      setShowNewPatient(false);
    }
  }, [open, defaultDate, clinics]);

  // Auto-fill duration from appointment type
  useEffect(() => {
    if (form.appointment_type_id) {
      const t = aptTypes.find(x => x.id === form.appointment_type_id);
      if (t) setForm(f => ({ ...f, duration_minutes: t.duration_minutes }));
    }
  }, [form.appointment_type_id, aptTypes]);

  const clinicRooms = useMemo(() => rooms.filter(r => !form.clinic_id || r.clinic_id === form.clinic_id), [rooms, form.clinic_id]);
  const clinicAudios = useMemo(() => employees.filter(
    e => e.role === 'audiologist' && (!form.clinic_id || (e.clinic_ids || []).includes(form.clinic_id))
  ), [employees, form.clinic_id]);

  const start = form.start_ts ? new Date(form.start_ts) : null;
  const end = start ? new Date(start.getTime() + form.duration_minutes * 60000) : null;

  const submit = async () => {
    if (!form.patient_id || !form.appointment_type_id || !form.clinic_id || !form.start_ts) {
      toast.error('Please fill required fields');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_ts: start.toISOString(),
        end_ts: end.toISOString(),
        audiologist_id: form.audiologist_id || null,
        room_id: form.room_id || null,
        state: 'created',
      };
      const url = override ? '/appointments?override=true' : '/appointments';
      const { data } = await api.post(url, payload);
      toast.success('Appointment created');
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.conflicts) {
        setConflicts(detail.conflicts);
        toast.warning('Conflict detected — review and override if needed');
      } else {
        toast.error(detail || 'Failed to create appointment');
      }
    } finally {
      setBusy(false);
    }
  };

  const createPatient = async () => {
    if (!newPatient.full_name || !newPatient.mobile) {
      toast.error('Name and mobile required');
      return;
    }
    // Client-side phone validation — 10 digits starting with 6-9 (India)
    const digits = String(newPatient.mobile).replace(/\D+/g, '').replace(/^0+/, '').replace(/^91(?=\d{10}$)/, '');
    if (!/^[6-9]\d{9}$/.test(digits)) {
      toast.error(`Invalid mobile "${newPatient.mobile}" — must be 10 digits starting with 6-9`);
      return;
    }
    try {
      const { data } = await api.post('/patients', {
        ...newPatient,
        mobile: digits,
        age: newPatient.age ? parseInt(newPatient.age) : null,
      });
      toast.success('Patient created');
      onPatientCreated && onPatientCreated(data);
      setForm((f) => ({ ...f, patient_id: data.id }));
      setShowNewPatient(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create patient'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={CAL.createDialog}>
        <DialogHeader>
          <DialogTitle className="font-heading">New Appointment</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Patient — searchable typeahead (never renders more than 20 rows
              at once → prevents the "hang on New Appointment" bug with 47k+
              patient DB). */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600">Patient *</Label>
            <div className="flex gap-1.5">
              <PatientTypeahead
                value={form.patient_id}
                seedPatients={patients}
                onChange={(pid) => setForm({ ...form, patient_id: pid })}
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setShowNewPatient(!showNewPatient)}
                className="h-9 shrink-0"
                data-testid="create-apt-new-patient-toggle"
              >
                <UserPlus className="w-4 h-4" />
              </Button>
            </div>

            {showNewPatient && (
              <div className="border border-stone-200 rounded-md p-3 mt-2 space-y-2 bg-stone-50">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">New patient</div>
                <Input placeholder="Full name *" value={newPatient.full_name} onChange={(e) => setNewPatient({...newPatient, full_name: e.target.value})} className="h-8" data-testid="new-patient-name" />
                <Input placeholder="Mobile *" value={newPatient.mobile} onChange={(e) => setNewPatient({...newPatient, mobile: e.target.value})} className="h-8" data-testid="new-patient-mobile" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Age" type="number" value={newPatient.age} onChange={(e) => setNewPatient({...newPatient, age: e.target.value})} className="h-8" />
                  <Select value={newPatient.gender} onValueChange={(v) => setNewPatient({...newPatient, gender: v})}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="w-full h-8 bg-stone-900 hover:bg-stone-800 text-white" onClick={createPatient} data-testid="new-patient-submit">
                  Create Patient
                </Button>
              </div>
            )}
          </div>

          {/* Appointment type */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600">Appointment Type *</Label>
            <Select value={form.appointment_type_id} onValueChange={(v) => setForm({ ...form, appointment_type_id: v })}>
              <SelectTrigger className="h-9" data-testid="create-apt-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {aptTypes.filter(t => t.active).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} · {t.duration_minutes} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clinic + channel */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Clinic *</Label>
              <Select value={form.clinic_id} onValueChange={(v) => setForm({ ...form, clinic_id: v, audiologist_id: '', room_id: '' })}>
                <SelectTrigger className="h-9" data-testid="create-apt-clinic-select">
                  <SelectValue placeholder="Clinic" />
                </SelectTrigger>
                <SelectContent>
                  {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_clinic">In Clinic</SelectItem>
                  <SelectItem value="home_visit">Home Visit</SelectItem>
                  <SelectItem value="teleconsultation">Teleconsultation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Audio + room */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Audiologist</Label>
              <Select value={form.audiologist_id || 'none'} onValueChange={(v) => setForm({ ...form, audiologist_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Assign later" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {clinicAudios.map((e) => (<SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Room</Label>
              <Select value={form.room_id || 'none'} onValueChange={(v) => setForm({ ...form, room_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No room</SelectItem>
                  {clinicRooms.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Start + duration */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Start *</Label>
              <Input type="datetime-local" value={form.start_ts} onChange={(e) => setForm({ ...form, start_ts: e.target.value })} className="h-9" data-testid="create-apt-start-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Duration (min)</Label>
              <Input type="number" min={5} step={5} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} className="h-9" />
            </div>
          </div>

          {/* Priority */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-600">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {conflicts.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-2">
              <div className="flex items-center gap-1.5 text-amber-800 text-xs font-semibold uppercase tracking-wider">
                <AlertTriangle className="w-3.5 h-3.5" /> {conflicts.length} conflict(s) detected
              </div>
              <ul className="text-xs text-amber-900 mt-1 space-y-0.5">
                {conflicts.map((c) => (
                  <li key={c.id}>· {new Date(c.start_ts).toLocaleString()} — {c.audiologist_id ? 'audiologist' : 'room'}</li>
                ))}
              </ul>
              <div className="flex items-center gap-2 mt-2">
                <Switch id="override" checked={override} onCheckedChange={setOverride} data-testid="create-apt-override" />
                <Label htmlFor="override" className="text-xs">Override & create anyway (audited)</Label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || (conflicts.length > 0 && !override)}
            className="h-9 bg-stone-900 hover:bg-stone-800 text-white"
            data-testid={CAL.createDialogSubmit}
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
